import { Link, useParams } from "react-router-dom";
import { getQuizzes, getTenant, type QuizInfo, type TenantDetail as TenantDetailData } from "@/api";
import { useAuth } from "@/auth";
import { useResource } from "@/hooks/useResource";
import { EmbedButton } from "@/components/EmbedButton";
import { PreviewButton } from "@/components/PreviewButton";

interface ClientWorkspace {
  tenant: TenantDetailData;
  quizzes: QuizInfo[];
}

/** Client workspace: lists one client's quizzes, each with Preview, Copy Code,
 * and a deep-link into its analytics. "Client" is the user-facing name for a
 * tenant; the route param + API stay `tenant`. */
export function TenantDetail() {
  const { key = "" } = useParams();
  const { status } = useAuth();
  // The api client reads its own creds; useResource gates only on the route
  // param (`enabled`), never on creds — the Google cookie lane loads alike.
  const { data, loading, error: loadError } = useResource<ClientWorkspace>(
    () =>
      Promise.all([getTenant(key), getQuizzes(key)]).then(
        ([tenant, quizList]) => ({ tenant, quizzes: quizList.quizzes }),
      ),
    [status, key],
    { enabled: Boolean(key) },
  );
  const error = loadError && "Could not load this client.";
  const name = data?.tenant.name ?? null;
  const quizzes = data?.quizzes ?? [];

  return (
    <div className="space-y-8">
      <div className="rise">
        <Link to="/tenants" className="text-sm text-dim hover:text-text">← Clients</Link>
        <h1 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight">
          {name ?? key}
        </h1>
        <p className="mt-1 text-sm text-dim">
          <span className="tag">{key}</span>
        </p>
      </div>

      {error && <p role="alert" className="text-coral">{error}</p>}

      {!error && loading && <p className="text-dim">Loading…</p>}

      {!error && !loading && quizzes.length === 0 && (
        <p className="text-dim">No quizzes yet.</p>
      )}

      {!error && !loading && quizzes.length > 0 && (
        <ul className="rise card divide-y divide-line overflow-hidden">
          {quizzes.map((q) => (
            <li key={q.slug} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <div className="min-w-0">
                <span className="font-mono text-text">{q.slug}</span>
                <span className="ml-3 text-sm text-dim">
                  {q.personas.length} {q.personas.length === 1 ? "outcome" : "outcomes"}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <PreviewButton slug={q.slug} />
                <EmbedButton slug={q.slug} />
                <Link
                  to={`/?tenant=${encodeURIComponent(key)}&quiz=${encodeURIComponent(q.slug)}`}
                  className="btn-outline"
                >
                  View analytics
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
